import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Button,
  Divider,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useNavigate } from 'react-router-dom';
import notificationsApi, { Notification as AppNotification } from '../api/notifications';
import { useAuth } from '../contexts/AuthContext';
import { initPushNotifications, requestAndSubscribe, getPushState, isPushRegistered } from '../utils/pushManager';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

const POLL_INTERVAL = 15000; // 15 seconds for near-real-time

// Play a short notification tone using Web Audio API (no file needed)
const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880; // A5 note
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
    // Second beep
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.frequency.value = 1100; // C#6 note
    osc2.type = 'sine';
    gain2.gain.setValueAtTime(0.3, ctx.currentTime + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.45);
    osc2.start(ctx.currentTime + 0.15);
    osc2.stop(ctx.currentTime + 0.45);
  } catch {
    // Audio not supported
  }
};

export default function NotificationBell() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [toastNotification, setToastNotification] = useState<AppNotification | null>(null);
  const [pushState, setPushState] = useState<'unsupported' | 'default' | 'granted' | 'denied'>('default');
  const [enablingPush, setEnablingPush] = useState(false);
  const prevUnreadCount = useRef(0);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const count = await notificationsApi.getUnreadCount();
      
      // If count increased, there's a new notification — play sound + show toast
      if (count > prevUnreadCount.current && prevUnreadCount.current >= 0) {
        playNotificationSound();
        // Fetch the latest notification to show as toast
        try {
          const data = await notificationsApi.getAll({ limit: 1, unread_only: true });
          if (data.notifications.length > 0) {
            setToastNotification(data.notifications[0]);
          }
        } catch {
          // Toast fetch failed — not critical
        }
      }
      
      prevUnreadCount.current = count;
      setUnreadCount(count);
    } catch {
      // Silently fail
    }
  }, [isAuthenticated]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationsApi.getAll({ limit: 20 });
      setNotifications(data.notifications);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    // Initialize prevUnreadCount to -1 so first fetch doesn't trigger sound
    prevUnreadCount.current = -1;
    fetchUnreadCount().then(() => {
      prevUnreadCount.current = unreadCount;
    });
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);

    // Initialize FCM push notifications (auto-subscribes if permission already granted)
    initPushNotifications()
      .then(() => setPushState(getPushState()))
      .catch(err => console.error('[Push] Init failed:', err));

    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.action === 'navigate' && event.data.url) {
        navigate(event.data.url);
        fetchUnreadCount();
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);

    // Show a browser notification when user leaves/minimizes the app with unread messages
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && unreadCount > 0 && 'Notification' in window && Notification.permission === 'granted') {
        const n = new Notification('StitchFlo', {
          body: `You have ${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`,
          icon: '/icons/icon-192x192.png',
          tag: 'stitchflo-unread',
          renotify: false
        });
        n.onclick = () => {
          window.focus();
          navigate('/designs/chat');
          n.close();
        };
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUnreadCount, isAuthenticated, navigate, unreadCount]);

  // Fix: update prevUnreadCount after initial load
  useEffect(() => {
    if (prevUnreadCount.current === -1 && unreadCount >= 0) {
      prevUnreadCount.current = unreadCount;
    }
  }, [unreadCount]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = async (notification: AppNotification) => {
    if (!notification.is_read) {
      await notificationsApi.markAsRead(notification.id);
      setUnreadCount(prev => Math.max(0, prev - 1));
      prevUnreadCount.current = Math.max(0, prevUnreadCount.current - 1);
      setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n));
    }
    handleClose();
    if (notification.design_no) {
      navigate(`/designs/chat?designNo=${encodeURIComponent(notification.design_no)}`);
    }
  };

  const handleToastClick = () => {
    if (toastNotification?.design_no) {
      navigate(`/designs/chat?designNo=${encodeURIComponent(toastNotification.design_no)}`);
    }
    setToastNotification(null);
  };

  const handleMarkAllRead = async () => {
    await notificationsApi.markAllAsRead();
    setUnreadCount(0);
    prevUnreadCount.current = 0;
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const handleEnablePush = async () => {
    setEnablingPush(true);
    try {
      const success = await requestAndSubscribe();
      setPushState(getPushState());
      if (!success) {
        console.log('[Push] User denied or failed to enable push notifications');
      }
    } catch (err) {
      console.error('[Push] Enable push failed:', err);
    } finally {
      setEnablingPush(false);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} size="small">
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 480, borderRadius: 2 } } }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700}>Notifications</Typography>
          {unreadCount > 0 && (
            <Button size="small" startIcon={<DoneAllIcon />} onClick={handleMarkAllRead} sx={{ textTransform: 'none' }}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {pushState === 'default' && (
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              startIcon={enablingPush ? <CircularProgress size={14} /> : <NotificationsActiveIcon />}
              onClick={handleEnablePush}
              disabled={enablingPush}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              {enablingPush ? 'Enabling...' : 'Enable Push Notifications'}
            </Button>
          </Box>
        )}
        {pushState === 'denied' && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Push notifications blocked. Enable in browser settings.
            </Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No notifications yet</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map(n => (
              <ListItemButton
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                sx={{
                  bgcolor: n.is_read ? 'transparent' : 'action.hover',
                  borderLeft: n.is_read ? 'none' : '3px solid',
                  borderLeftColor: 'primary.main',
                  py: 1.5, px: 2
                }}
              >
                <ChatBubbleOutlineIcon sx={{ mr: 1.5, fontSize: 18, color: 'primary.main', mt: 0.3 }} />
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={n.is_read ? 400 : 600} sx={{ lineHeight: 1.3 }}>
                      {n.title}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                        {n.message}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                        {formatTime(n.created_at)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>

      {/* Toast notification for new messages */}
      <Snackbar
        open={!!toastNotification}
        autoHideDuration={5000}
        onClose={() => setToastNotification(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 7 }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setToastNotification(null)}
          onClick={handleToastClick}
          sx={{ cursor: 'pointer', minWidth: 280 }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
            {toastNotification?.title}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {toastNotification?.message}
          </Typography>
        </Alert>
      </Snackbar>
    </>
  );
}
